<script setup lang="ts">
import { ref, watch } from 'vue';
import { ActionSheet, CellGroup, Field, RadioGroup, Radio, Button, showToast } from 'vant';
import { useParamStore } from '@/store/param.ts';
import { useRelationStore } from '@repo/stores';
import { type Shape, useStyle3d } from '@repo/style3d';
import { base64ToFile, generateRandomUUID } from '@repo/utils';
import { uploadFileViaStsToken } from '@/api';
import { order } from '@/api/exhibition';
import { useLoading } from '@repo/components';

const show = defineModel<boolean>('show');

const props = defineProps<{ enterpriseId: string }>();

const contact = ref({
  name: '',
  tel: '',
  address: '',
  gender: '',
  height: '',
  weight: ''
});

const savedContact = localStorage.getItem('style-order-contact');
if (savedContact) {
  try {
    const savedContactObj = JSON.parse(savedContact);
    if (savedContactObj) {
      contact.value = { ...savedContactObj };
    }
  } catch (e) {
    console.log(e);
  }
}

const relationStore = useRelationStore();
const size = ref<{ name: string, value: number }[]>([]);

watch(() => relationStore?.relation?.size, () => {
  size.value = [...(relationStore?.relation?.size || [])];
}, {
  immediate: true
});

const remark = ref('');

const paramStore = useParamStore();

const keyGenerator = (item: Shape['fabric']) => {
  if (!item) return '';
  return `${item.id}_${item?.type}_${item?.originUrl || ''}`;
};

const style3d = useStyle3d();

const loadingSpin = useLoading();

const prepareOrderJson = async (key: string) => {
  const sizeArr = [...size.value.map(item => ({ ...item, value: Number(item.value) }))];

  const style3dSDK = style3d.getStyle3dSDKInstance();
  const info: any = {
    'version': '1.0.0',
    'sources': [],
    'pieces': [],
    'size': [
      ...sizeArr
    ],
    'parameters': {}
  };


  const decalList = await style3dSDK.getDecalList();
  const fabricDecalMap: any = {};
  for (const decal of decalList) {
    info.sources.push({ id: decal.uuid, type: 'image', originUrl: decal.path });

    const shapeId = decal.fabricInfo?.id;
    if (!shapeId) {
      continue;
    }
    if (!fabricDecalMap[shapeId]) {
      fabricDecalMap[shapeId] = [];
    }
    fabricDecalMap[shapeId].push({
      id: decal.uuid,
      pos: {
        x: 0,
        y: 0,
        w: decal.exportDecalState.originWidth,
        h: decal.exportDecalState.originHeight
      }
    });
  }

  const map = new Map<string, any>();
  for (const shape of relationStore.shapeList) {
    if (!shape.fabric) {
      continue;
    }

    if (!map.has(keyGenerator(shape.fabric))) {
      const fabricId = generateRandomUUID();
      map.set(keyGenerator(shape.fabric), { ...shape.fabric, uuid: fabricId, color: shape?.color });
    }

    const fabricId = map.get(keyGenerator(shape.fabric)).uuid;
    const shapeId = shape.id;

    info.pieces.push({
      id: shapeId,
      name: shape.piece
    });

    info.parameters[shapeId] = {
      fabricId,
      element: [
        ...(fabricDecalMap[shapeId] || [])
      ]
    };
  }

  const scoResource = paramStore.resourceParam.scoResource;
  const sco = {
    id: generateRandomUUID(),
    url: scoResource?.originUrl || '',
    type: 'project'
  };
  info.sources.push(sco);

  map.forEach(value => {
    let fabric: any = {
      id: value.uuid,
      type: 'fabric',
      url: value.originUrl
    };
    if (value.color) {
      fabric.parameters = {
        colors: [{ color: value.color }]
      };
    }
    info.sources.push(fabric);
  });

  const blob = new Blob([JSON.stringify(info, null, 2)], { type: 'application/json' });
  const file = new File([blob], 'order.json', { type: 'application/json' });

  const orderJsonPath = await uploadFileViaStsToken(`${key}/order.json`, file, {
    bucket: 'kd-order',
    region: 'oss-cn-shanghai'
  });

  return orderJsonPath;
};

const takeScreenshot = async (key: string) => {
  const side = await style3d.getScreenshot(2);
  const sideScreenshot = base64ToFile(side, '侧面图.png');
  const screenshot1 = await uploadFileViaStsToken(`${key}/侧面图.png`, sideScreenshot, {
    bucket: 'kd-order',
    region: 'oss-cn-shanghai'
  });

  const back = await style3d.getScreenshot(4);
  const backScreenshot = base64ToFile(back, '背面图.png');
  const screenshot2 = await uploadFileViaStsToken(`${key}/背面图.png`, backScreenshot, {
    bucket: 'kd-order',
    region: 'oss-cn-shanghai'
  });

  const front = await style3d.getScreenshot(1);
  const frontScreenshot = base64ToFile(front, '正面图.png');
  const screenshot3 = await uploadFileViaStsToken(`${key}/正面图.png`, frontScreenshot, {
    bucket: 'kd-order',
    region: 'oss-cn-shanghai'
  });
  return { screenshot1, screenshot2, screenshot3 };
};
const onSubmit = async () => {
  loadingSpin.setLoading(true);

  try {
    const sizeArr = [...size.value.map(item => ({ ...item, value: Number(item.value) }))];
    const date = new Date();
    const key = `/sv-000006/${date.getFullYear()}${date.getMonth() + 1}${date.getDate()}-${generateRandomUUID()}`;
    const { screenshot1, screenshot2, screenshot3 } = await takeScreenshot(key);
    const orderJsonPath = await prepareOrderJson(key);

    await order(props.enterpriseId, {
      'effect_link': '',
      'screen_shot': [
        // 顺序影响到轻流默认显示的图片顺序
        decodeURIComponent(screenshot3),
        decodeURIComponent(screenshot1),
        decodeURIComponent(screenshot2)
      ],
      'profile': orderJsonPath,
      'name': contact.value.name,
      'phone': contact.value.tel,
      'remark': remark.value,
      'size': [...sizeArr]
    });

    showToast('下单成功');
    show.value = false;
  } catch (e: any) {
    showToast({
      message: e.message || '下单失败',
      type: 'fail'
    });
  } finally {
    loadingSpin.setLoading(false);
  }
  localStorage.setItem('style-order-contact', JSON.stringify(contact.value));
};
</script>

<template>
  <ActionSheet v-model:show="show" title="下单" teleport="#app">
    <div :class="$style.content">
      <div style="overflow: auto;">
        <CellGroup title="联系人">
          <Field v-model="contact.name" label="姓名" />
          <Field v-model="contact.tel" label="电话" />
          <Field v-model="contact.address" label="地址" />
          <Field name="radio" label="性别">
            <template #input>
              <RadioGroup v-model="contact.gender" direction="horizontal">
                <Radio name="male">男</Radio>
                <Radio name="female">女</Radio>
                <Radio name="">未设置</Radio>
              </RadioGroup>
            </template>
          </Field>
          <Field v-model="contact.height" type="digit" label="身高" />
          <Field v-model="contact.weight" type="digit" label="体重" />
        </CellGroup>
        <CellGroup title="尺寸表">
          <Field
            v-for="s of size"
            type="number"
            v-model="s.value"
            :label="s.name"
            :key="s.name"
          />
          <Field
            v-model="remark"
            type="textarea"
            rows="2"
            label="备注"
            autosize
          />
        </CellGroup>
      </div>
      <div :class="$style.buttonContainer">
        <Button type="primary" style="width: 100%;" @click="onSubmit">下单</Button>
      </div>
    </div>
  </ActionSheet>
</template>

<style module>
.content {
  padding: 10px 30px 60px;
  display: flex;
  flex-direction: column;
  min-height: 230px;
  max-height: 450px;
  position: relative;
}

.buttonContainer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 3px 10px;
}
</style>

